import { FLAT_RATE_PLANS } from "@common/flatRateBilling";
import { WorkspaceTimezone } from "@common/workspaceTimezone";
import { assertRelationLoaded } from "@common/helpers/orm";
import { formatISO, parseISO } from "date-fns";
import { Workspace, WorkspaceBase } from "../../types/workspace";
import { EntityDto } from "../entity/entity.dto";
import { WorkspaceUserAssignment } from "../../types/workspaceUserAssignment";
import { unpackDateOrNull } from "../dto-helpers";
import { getPrimaryAdminUserIdFromAssignments } from "../../helpers/workspace-primary-admin";

/**
 * Input type for WorkspaceDto constructor.
 * Accepts Base type from entity classes - relations validated at runtime.
 */
export type WorkspaceDtoInput = WorkspaceBase;

export class WorkspaceDto {
    static deserialize(dto: WorkspaceDto): Workspace {
        return {
            ...dto,
            entities: dto.entities?.map(EntityDto.deserialize),
            createdAt: parseISO(dto.createdAt),
            onboardingCompletedAt: unpackDateOrNull(dto.onboardingCompletedAt),
            glFirstEnabledAt: unpackDateOrNull(dto.glFirstEnabledAt),
            demoDataSeedingStartedAt: unpackDateOrNull(
                dto.demoDataSeedingStartedAt,
            ),
            demoDataSeedingFinishedAt: unpackDateOrNull(
                dto.demoDataSeedingFinishedAt,
            ),
        };
    }

    id: string;
    name: string;
    primaryAdminUserId?: number | null;
    entities?: EntityDto[];
    userAssignments: WorkspaceUserAssignment[];
    isDemo: boolean;
    isFirmWorkspace: boolean;
    createdAt: string;
    onboardingCompletedAt: string | null;
    glFirstEnabledAt: string | null;
    demoDataSeedingStartedAt: string | null;
    demoDataSeedingFinishedAt: string | null;
    iconColor: string | null;
    plan: FLAT_RATE_PLANS | null;
    planBeforeTransfer: FLAT_RATE_PLANS | null;
    bookkeepingStartDate: string;
    bookkeepingEndDate: string | null;
    freeUsageLimit: number;
    tempMigratedToNormalizedDescriptions: boolean;
    tempMigratedToReasoningCategorization: boolean;
    tempMigratedToSimilarityRules: boolean;
    isFreshbooksCustomer: boolean;
    showAccountNumbers: boolean;
    isDateOnlyMigrated: boolean;
    // Computed from `glFirstEnabledAt`. Agent surfaces consume this boolean to
    // detect GL-first workspaces; never the timestamp field.
    glFirstEnabled: boolean;
    timezone: WorkspaceTimezone;

    constructor(workspace: WorkspaceDtoInput) {
        assertRelationLoaded(workspace, "userAssignments");

        this.id = workspace.id;
        this.name = workspace.name;
        this.primaryAdminUserId = getPrimaryAdminUserIdFromAssignments(
            workspace.userAssignments,
        );
        this.entities = workspace.entities?.map((e) => new EntityDto(e));
        this.userAssignments =
            workspace.userAssignments as WorkspaceUserAssignment[];
        this.isDemo = workspace.isDemo;
        this.isFirmWorkspace = workspace.isFirmWorkspace;
        this.createdAt = formatISO(workspace.createdAt);
        this.onboardingCompletedAt =
            workspace.onboardingCompletedAt?.toISOString() ?? null;
        this.glFirstEnabledAt =
            workspace.glFirstEnabledAt?.toISOString() ?? null;
        this.demoDataSeedingStartedAt =
            workspace.demoDataSeedingStartedAt?.toISOString() ?? null;
        this.demoDataSeedingFinishedAt =
            workspace.demoDataSeedingFinishedAt?.toISOString() ?? null;
        this.iconColor = workspace.iconColor;
        this.plan = workspace.plan ?? null;
        this.planBeforeTransfer = workspace.planBeforeTransfer ?? null;
        this.bookkeepingStartDate = workspace.bookkeepingStartDate;
        this.bookkeepingEndDate = workspace.bookkeepingEndDate;
        this.freeUsageLimit = workspace.freeUsageLimit;
        this.tempMigratedToNormalizedDescriptions =
            workspace.tempMigratedToNormalizedDescriptions;
        this.tempMigratedToReasoningCategorization =
            workspace.tempMigratedToReasoningCategorization;
        this.tempMigratedToSimilarityRules =
            workspace.tempMigratedToSimilarityRules;
        this.isFreshbooksCustomer = workspace.isFreshbooksCustomer;
        this.showAccountNumbers = workspace.showAccountNumbers;
        this.isDateOnlyMigrated = workspace.isDateOnlyMigrated;
        this.glFirstEnabled = workspace.glFirstEnabledAt != null;
        this.timezone = workspace.timezone;
    }
}
